import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { pluggyContasApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { LoadingState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { Select } from '../../components/ui/Input'
import { useToast } from '../../components/ui/Toast'
import { formatDate } from '../../lib/utils'
import { mensagemErroPluggy } from '../../lib/pluggyErro'
import { invalidarPluggy, itemAbreviado, type OpcaoConexao } from './invalidar'
import type { DuplicidadeInfo, ExcluirDuplicadaResultadoInfo } from '../../types'

const PLUGGY_TIPO_CURTO: Record<string, string> = { BANK: 'Conta', CREDIT: 'Crédito' }


/**
 * Exclui uma conexão duplicada da Pluggy. Pergunta qual conexão manter (a menos que `mantidoFixo` venha),
 * mostra a prévia (vínculos transferidos, contas sem equivalente, corte) e só então confirma.
 * A conta do app, os lançamentos e o saldo nunca mudam.
 */
export function ExcluirDuplicada({ itemId, banco, rotuloItem, opcoes, sugerido, mantidoFixo, onClose }: {
  itemId: string
  banco: string
  rotuloItem: string
  /** Conexões que podem ser mantidas (todas, menos a que será excluída). */
  opcoes: OpcaoConexao[]
  sugerido?: string
  mantidoFixo?: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const toast = useToast()
  const [mantidoEscolhido, setMantidoEscolhido] = useState(mantidoFixo ?? sugerido ?? opcoes[0]?.itemId ?? '')
  const mantido = mantidoFixo ?? mantidoEscolhido
  const [manual, setManual] = useState<Record<string, string>>({})
  const [resultado, setResultado] = useState<ExcluirDuplicadaResultadoInfo | null>(null)

  const previa = useQuery({
    queryKey: ['pluggy-excluir-duplicada-previa', itemId, mantido],
    queryFn: () => pluggyContasApi.excluirDuplicadaPrevia(itemId, mantido),
    enabled: !!mantido && !resultado,
    retry: false,
    gcTime: 0,
  })

  const dados = previa.data
  const semEq = dados?.semEquivalente ?? []
  const todosEscolhidos = semEq.every((s) => !!manual[`${mantido}:${s.contaPluggyId}`])
  const bloqueado = !dados || (semEq.length > 0 && !todosEscolhidos)
  const rotuloMantido = opcoes.find((o) => o.itemId === mantido)?.rotulo ?? `conexão ${itemAbreviado(mantido)}`

  const excluir = useMutation({
    mutationFn: () => pluggyContasApi.excluirDuplicada(itemId, {
      mantidoItemId: mantido,
      mapeamento: semEq.length
        ? semEq.map((s) => ({ contaPluggyAntigaId: s.contaPluggyId, contaPluggyNovaId: manual[`${mantido}:${s.contaPluggyId}`] }))
        : undefined,
    }),
    onSuccess: (r) => {
      invalidarPluggy(qc)
      setResultado(r)
      if (r?.itemExcluidoNaPluggy !== false) toast.success('Conexão excluída.')
    },
    onError: (e: Error) => toast.error(mensagemErroPluggy(e, 'Não foi possível excluir a conexão.')),
  })

  if (resultado) {
    const falhou = resultado.itemExcluidoNaPluggy === false
    return (
      <Modal open onClose={onClose} title="Excluir conexão duplicada" footer={
        <>
          {falhou && <Button variant="soft-info" icon="ri-refresh-line" loading={excluir.isPending} onClick={() => excluir.mutate()}>Repetir exclusão</Button>}
          <Button variant="light" onClick={onClose}>Fechar</Button>
        </>
      }>
        <div className="space-y-3 text-13 text-muted">
          {(resultado.vinculosTransferidos ?? 0) > 0 && (
            <p><strong className="text-white">{resultado.vinculosTransferidos}</strong> vínculo(s) passaram para a conexão mantida.</p>
          )}
          {falhou ? (
            <p className="rounded border border-warning/40 bg-warning/10 p-3 text-warning" role="alert">
              {resultado.aviso || 'A conexão não foi excluída na Pluggy.'} Os vínculos já foram transferidos. Repita a exclusão para concluir.
            </p>
          ) : (
            <p>A conexão foi excluída. A conta do app, os lançamentos e o saldo não mudaram.</p>
          )}
        </div>
      </Modal>
    )
  }

  return (
    <Modal
      open onClose={onClose} title="Excluir conexão duplicada" size="lg"
      footer={
        <>
          <Button variant="light" onClick={onClose}>Cancelar</Button>
          <Button variant="danger" icon="ri-delete-bin-line" disabled={bloqueado} loading={excluir.isPending} onClick={() => excluir.mutate()}>
            {dados?.temVinculos ? 'Transferir e excluir' : 'Excluir conexão'}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-13 text-muted">
        <p>Será excluída: <strong className="text-white">{banco} · {rotuloItem}</strong>.</p>

        {mantidoFixo ? (
          <p>Será mantida: <strong className="text-white">{rotuloMantido}</strong>.</p>
        ) : (
          <Select label="Qual conexão manter?" value={mantidoEscolhido} onChange={(e) => setMantidoEscolhido(e.target.value)}>
            {opcoes.map((o) => <option key={o.itemId} value={o.itemId}>{o.rotulo}{o.itemId === sugerido ? ' (sugerida)' : ''}</option>)}
          </Select>
        )}

        {previa.isLoading ? <LoadingState /> : previa.isError ? (
          <p className="rounded bg-expense/10 px-3 py-2 text-expense" role="alert">{mensagemErroPluggy(previa.error, 'Não foi possível montar a prévia.')}</p>
        ) : dados && (
          <>
            {!dados.temVinculos && semEq.length === 0 ? (
              <p>Esta conexão não tem vínculos. Ela será excluída direto.</p>
            ) : (
              <>
                {dados.transferencias.length > 0 && (
                  <div>
                    <p className="mb-2 font-medium text-white">Vínculos que serão transferidos</p>
                    <ul className="space-y-2">
                      {dados.transferencias.map((t) => (
                        <li key={t.vinculoId} className="rounded border border-border/70 bg-surface2/40 p-3">
                          <span className="block text-white">{t.nomeContaApp}</span>
                          <span className="block">
                            {t.contaPluggyAtualNome} <i className="ri-arrow-right-line" aria-hidden /> {t.contaPluggyNovaNome}
                            {t.numeroFinal && ` (final ${t.numeroFinal})`}
                          </span>
                          <span className="block text-2xs">
                            Corte automático: a partir da última sincronização{t.dataCorte ? ` (${formatDate(t.dataCorte)})` : ''}.
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {semEq.length > 0 && (
                  <div>
                    <p className="mb-2 font-medium text-warning">Contas sem equivalente na conexão mantida</p>
                    <ul className="space-y-2">
                      {semEq.map((s) => (
                        <li key={s.contaPluggyId} className="rounded border border-warning/40 bg-warning/10 p-3">
                          <span className="block text-white">
                            {s.nomeContaApp}: {s.nome}{s.numeroFinal ? ` (final ${s.numeroFinal})` : ''}
                          </span>
                          {(s.candidatas?.length ?? 0) > 0 ? (
                            <Select
                              aria-label={`Equivalente de ${s.nome}`} value={manual[`${mantido}:${s.contaPluggyId}`] ?? ''}
                              onChange={(e) => setManual((m) => ({ ...m, [`${mantido}:${s.contaPluggyId}`]: e.target.value }))}
                            >
                              <option value="">Escolha a conta equivalente...</option>
                              {s.candidatas!.map((c) => (
                                <option key={c.contaPluggyId} value={c.contaPluggyId}>
                                  {c.nome}{c.numeroFinal ? ` (final ${c.numeroFinal})` : ''}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <span className="block">Não há conta equivalente para escolher. Não dá para excluir esta conexão.</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {(dados.avisos ?? []).filter((a) => !/saldo e os lan/i.test(a)).map((a, i) => <p key={i}>{a}</p>)}
            <p className="rounded border border-info/30 bg-info/10 p-3 text-info">
              <i className="ri-information-line" aria-hidden /> A conta do app, os lançamentos e o saldo não mudam.
            </p>
          </>
        )}
      </div>
    </Modal>
  )
}

/** Diálogo mostrado quando o banco que acabou de ser conectado já estava conectado. */
export function DialogoDuplicidade({ duplicidade, onEscolher, onClose }: {
  duplicidade: DuplicidadeInfo
  onEscolher: (acao: 'usar-nova' | 'manter-antiga') => void
  onClose: () => void
}) {
  const contas = duplicidade.contas
  return (
    <Modal
      open onClose={onClose} title="Este banco já estava conectado"
      footer={
        <>
          <Button variant="light" onClick={onClose}>Manter as duas</Button>
          <Button variant="soft-info" onClick={() => onEscolher('manter-antiga')}>Manter a antiga</Button>
          <Button onClick={() => onEscolher('usar-nova')}>Usar a nova</Button>
        </>
      }
    >
      <div className="space-y-3 text-13 text-muted">
        <p><strong className="text-white">{duplicidade.banco}</strong> já tinha uma conexão com estas contas:</p>
        <ul className="space-y-1">
          {contas.map((c) => (
            <li key={c.contaNovaId} className="text-white">
              {c.nome} <span className="text-muted">· {PLUGGY_TIPO_CURTO[c.tipo] ?? c.tipo}{c.numeroFinal ? ` · final ${c.numeroFinal}` : ''}</span>
            </li>
          ))}
        </ul>
        <ul className="space-y-1">
          <li><strong className="text-white">Usar a nova:</strong> exclui a conexão antiga e passa os vínculos para a nova.</li>
          <li><strong className="text-white">Manter a antiga:</strong> exclui a nova.</li>
          <li><strong className="text-white">Manter as duas:</strong> não muda nada. As contas ficam marcadas como possível duplicada.</li>
        </ul>
        <p>A conta do app, os lançamentos e o saldo não mudam.</p>
      </div>
    </Modal>
  )
}
